import { FC, useEffect, useMemo, useRef, useState } from 'react'
import ReportCardV2 from '../../components/report-card-v2/ReportCardV2'
import { ToastVariant, openToast, closeToast } from '../../../../../store/toasts/toastSlice'
import { downloadFile } from '../../../reports/reports.utils'
import DocumentChartBarIcon from '../../../../components/svg/DocumentChartBar'
import { useAppDispatch } from '../../../../../store/hooks'
import { hasAnyRole, Role } from '../../../../../lib/utils'
import { useAuth } from '@frontegg/react'
import { ReportServiceV1 } from 'services'
import toast from 'react-hot-toast'
import { HotToast } from 'ui/components/toast'

interface SExecutivePresentationProps {
  noActiveCpis?: boolean
}

const ExecutivePresentation: FC<SExecutivePresentationProps> = ({ noActiveCpis }) => {
  const { user } = useAuth()
  const dispatch = useAppDispatch()
  const closeNotification = useRef<any>(null)

  const [exeReportUrl, setExeReportUrl] = useState('')
  const [isGeneratingExeReport, setIsGeneratingExeReport] = useState(false)

  const generateExecutiveReport = async () => {
    if (noActiveCpis) {
      dispatch(
        openToast({
          variant: ToastVariant.warning,
          props: {
            text: 'Report Generation Error',
            description:
              'No active CPIs were found. Please activate CPIs to generate a report.'
          }
        })
      )
      closeNotification.current = setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
      return
    }

    try {
      setIsGeneratingExeReport(true)
      const res = await ReportServiceV1.generateExecutiveReport()
      if (res.status.toString().startsWith('2')) {
        const { downloadURL } = res.data
        setExeReportUrl(downloadURL)
      }
    } catch (e) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to Export Report',
            description: 'Please Contact Our Support Team'
          }
        })
      )
    } finally {
      setIsGeneratingExeReport(false)
    }
  }

  const hasPermissionToGenerateReport = useMemo(() => {
    const allowedRoles = [Role.Owner, Role.Admin, Role.Demo]
    const hasRole = hasAnyRole(allowedRoles, user)
    return hasRole
  }, [user])

  useEffect(() => {
    if (exeReportUrl) {
      downloadFile(`${exeReportUrl}`, 'Executive Presentation')
      toast.success(() => <HotToast text="Executive Presentation Successfully Downloaded" />)
      setExeReportUrl('')
    }
  }, [exeReportUrl])

  return (
    <>
      <ReportCardV2
        reportIcon={<DocumentChartBarIcon />}
        title="Executive Presentation"
        description="PPT presentation for board reporting containing key highlights from Onyxia Platform."
        isLoading={isGeneratingExeReport}
        hasPermission={hasPermissionToGenerateReport}
        onDownload={generateExecutiveReport}
      />
    </>
  )
}

export default ExecutivePresentation
